import type { AssetRef } from '@repo/api-contracts';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { cn } from '@/shared/ui';

interface GalleryImage extends AssetRef {
  readonly displayOrder: number;
}

interface ProductImageGalleryProps {
  readonly productName: string;
  /** 대표사진(최대 5장, displayOrder 순). 라벨 없이 순서만 보여준다(확정 결정). */
  readonly images: readonly GalleryImage[];
  /** 선택한 색상의 미리보기 — null 이면 대표사진 1번 폴백. 색상 변경 시 부모가 key 로 리마운트한다. */
  readonly previewImage: AssetRef | null;
}

/** 원형 화살표 버튼 — 각진 사진 칸 좌우 끝에 얹는 작은 조작부. */
const ARROW_CLASS =
  'absolute top-1/2 inline-flex size-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-background/90 text-foreground shadow-sm transition-colors hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/**
 * 상세 좌측 갤러리 — 메인 = 썸네일·화살표 선택 > 색상 미리보기 > 대표 1번 순으로 결정.
 * 화살표·진행 막대는 넘길 사진이 2장 이상일 때만 단다. 순서는 [색상 미리보기, 대표사진…] 이고 양 끝에서 돌아 나간다.
 */
export const ProductImageGallery = ({
  productName,
  images,
  previewImage,
}: ProductImageGalleryProps): ReactElement => {
  const [activeUrl, setActiveUrl] = useState<string | null>(null);

  // 색상 미리보기가 대표사진과 같은 파일이면 한 장으로 센다.
  const slides: readonly AssetRef[] =
    previewImage !== null && !images.some((image) => image.url === previewImage.url)
      ? [previewImage, ...images]
      : images;
  const foundIndex = activeUrl === null ? -1 : slides.findIndex((slide) => slide.url === activeUrl);
  const activeIndex = foundIndex === -1 ? 0 : foundIndex;
  const mainUrl = slides[activeIndex]?.url ?? null;
  const canSlide = slides.length > 1;

  const goTo = (index: number): void => {
    const next = slides[(index + slides.length) % slides.length];
    if (next !== undefined) {
      setActiveUrl(next.url);
    }
  };

  return (
    // lg 미만 1열에서는 본문 전폭 정사각형이 되지 않도록 폭을 묶는다.
    <div className="mx-auto w-full max-w-xl space-y-3 lg:max-w-none">
      <div className="relative aspect-square w-full overflow-hidden bg-muted/70">
        {mainUrl !== null && (
          <img src={mainUrl} alt={productName} className="size-full object-cover" />
        )}

        {canSlide && (
          <>
            <button
              type="button"
              aria-label="이전 이미지"
              onClick={() => goTo(activeIndex - 1)}
              className={cn(ARROW_CLASS, 'left-4')}
            >
              <ChevronLeft aria-hidden className="size-5" strokeWidth={1.5} />
            </button>
            <button
              type="button"
              aria-label="다음 이미지"
              onClick={() => goTo(activeIndex + 1)}
              className={cn(ARROW_CLASS, 'right-4')}
            >
              <ChevronRight aria-hidden className="size-5" strokeWidth={1.5} />
            </button>

            {/* 사진 칸 아래 끝의 얇은 진행 막대 — 지금 몇 번째 장인지 길이로 보여 준다. */}
            <div
              role="progressbar"
              aria-label="이미지 위치"
              aria-valuemin={1}
              aria-valuemax={slides.length}
              aria-valuenow={activeIndex + 1}
              aria-valuetext={`${slides.length}장 중 ${activeIndex + 1}번째`}
              className="absolute inset-x-0 bottom-0 h-0.5 bg-foreground/10"
            >
              <div
                className="h-full bg-foreground transition-[width] duration-300 ease-out motion-reduce:transition-none"
                style={{ width: `${((activeIndex + 1) / slides.length) * 100}%` }}
              />
            </div>
          </>
        )}
      </div>

      {images.length > 0 && (
        <ul className="flex gap-2">
          {images.map((image, index) => (
            <li key={image.key}>
              <button
                type="button"
                aria-label={`${productName} 이미지 ${index + 1}`}
                aria-pressed={mainUrl === image.url}
                onClick={() => setActiveUrl(image.url)}
                className={cn(
                  'block size-16 cursor-pointer overflow-hidden bg-muted/70 transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  mainUrl === image.url
                    ? 'ring-1 ring-foreground ring-offset-2'
                    : 'opacity-70 hover:opacity-100',
                )}
              >
                <img src={image.url} alt="" className="size-full object-cover" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
